import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { authUtils } from '../../utils/auth';
import { API_ENDPOINTS } from '../../config/api';
import '../../App.css';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'system';
  timestamp: Date;
  attachment?: File;
}

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Helper function to check if content contains HTML
  const containsHTML = (content: string): boolean => {
    const htmlRegex = /<[^>]*>/g;
    return htmlRegex.test(content);
  };

  // Helper function to clean excessive whitespace from content
  const cleanContentWhitespace = (content: string): string => {
    if (!content) return content;

    // Check if content has HTML tags
    const hasHTMLTags = /<[^>]+>/g.test(content);

    if (hasHTMLTags) {
      // For HTML content, reduce excessive spacing but preserve necessary structure
      let cleaned = content;

      // Remove excessive newlines (3+ become 2)
      cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

      // Remove multiple consecutive spaces/tabs
      cleaned = cleaned.replace(/[ \t]{2,}/g, ' ');

      // Clean up excessive whitespace around HTML tags but preserve some structure
      cleaned = cleaned.replace(/>\s{2,}</g, '>\n<');
      cleaned = cleaned.replace(/>\s{2,}/g, '> ');
      cleaned = cleaned.replace(/\s{2,}</g, ' <');

      // Remove trailing spaces from lines but keep line structure
      const lines = cleaned.split('\n');
      const cleanedLines = lines.map(line => line.trim());
      cleaned = cleanedLines.join('\n');

      // Final cleanup: ensure no more than 2 consecutive newlines
      cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

      return cleaned.trim();
    } else {
      // For plain text, preserve some structure but reduce excessive spacing
      let cleaned = content.replace(/\n{3,}/g, '\n\n');
      cleaned = cleaned.replace(/[ \t]{2,}/g, ' ');
      return cleaned.trim();
    }
  };
  
  // Extract job details that appear immediately before a specific Apply link
  const extractJobDetailsBeforeLink = (content: string, linkHref: string) => {
    // Find the position of the specific Apply link in the content
    const linkPattern = new RegExp(`<a[^>]*href="${linkHref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>.*?</a>`, 'i');
    const linkMatch = content.match(linkPattern);
    
    if (!linkMatch) {
      console.warn('Could not find Apply link in content');
      return { jobTitle: null, company: null };
    }
    
    // Get the content before this specific link
    const linkIndex = content.indexOf(linkMatch[0]);
    const contentBeforeLink = content.substring(0, linkIndex);
    
    // Find the last occurrence of Job Title and Company before this link
    const jobTitleRegex = /<b>Job Title:\s*<\/b>\s*([^<\n]+)/gi;
    const companyRegex = /<b>Company:\s*<\/b>\s*([^<\n]+)/gi;
    
    let jobTitleMatch;
    let lastJobTitleMatch = null;
    while ((jobTitleMatch = jobTitleRegex.exec(contentBeforeLink)) !== null) {
      lastJobTitleMatch = jobTitleMatch;
    }
    
    let companyMatch;
    let lastCompanyMatch = null;
    while ((companyMatch = companyRegex.exec(contentBeforeLink)) !== null) {
      lastCompanyMatch = companyMatch;
    }
    
    const jobTitle = lastJobTitleMatch ? lastJobTitleMatch[1].trim() : null;
    const company = lastCompanyMatch ? lastCompanyMatch[1].trim() : null;
    
    return { jobTitle, company };
  };
  
  useEffect(() => {
    if (contentRef.current) {
      // Find all Apply links in the message
      const applyLinks = contentRef.current.querySelectorAll('a');
      
      applyLinks.forEach(link => {
        // Check if this is an Apply button/link
        if (link.textContent && (link.textContent.toLowerCase().includes('apply') || link.textContent.toLowerCase().includes('Apply to the job'))) {
          const jobUrl = link.getAttribute('href');
          
          if (jobUrl) {
            // Extract job details that appear immediately before this specific Apply link
            const { jobTitle, company } = extractJobDetailsBeforeLink(cleanContentWhitespace(message.content), jobUrl);
            
            // Override the default click behavior
            link.addEventListener('click', (e) => {
              e.preventDefault();
              
              console.log('🔍 Apply button clicked from chat message');
              console.log('🔗 Clicked Job URL:', jobUrl);
              console.log('👔 Extracted job title for this link:', jobTitle);
              console.log('🏢 Extracted company for this link:', company);
              console.log('📄 Full message content:', message.content);
              
              // Open the job URL immediately for better UX
              window.open(jobUrl, '_blank');
              
              // Track the job if we have the required details - asynchronously
              if (jobTitle && company) {
                console.log('✅ Job details found, tracking application...');
                const trackJobApplication = async () => {
                  try {
                    const token = authUtils.getToken();
                    console.log('🔑 Token available:', !!token);
                    
                    const payload = {
                      job_title: jobTitle,
                      job_url: jobUrl,
                      company_name: company,
                      status: 'Viewed',
                    };
                    console.log('📦 Tracking payload:', payload);
                    
                    await axios.post(
                      `${API_ENDPOINTS.JOB_TRACKER.BASE_URL}/api/jobs`,
                      payload,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );
                    console.log('✅ Job tracking successful');
                  } catch (error: any) {
                    console.error('❌ Failed to track job application:', error);
                    console.error('Error details:', error.response?.data);
                  }
                };
                
                // Execute tracking without blocking
                trackJobApplication();
              } else {
                console.warn('⚠️ Missing job details - cannot track');
                console.log('Job Title found:', !!jobTitle);
                console.log('Company found:', !!company);
              }
            });
          }
        }
      });
    }
  }, [message.content]);
  
  // Helper function to safely render HTML content
  const renderContent = (content: string) => {
    // Clean excessive whitespace first
    const cleanedContent = cleanContentWhitespace(content);

    if (containsHTML(cleanedContent)) {
      // Sanitize HTML and render it
      const sanitizedHTML = DOMPurify.sanitize(cleanedContent, {
        ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'ul', 'ol', 'li', 'a', 'p', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
        ALLOW_DATA_ATTR: false,
        FORCE_BODY: true,
        RETURN_DOM: false,
        RETURN_TRUSTED_TYPE: false
      });
      
      // Process the HTML to ensure all links open in new windows
      const processedHTML = sanitizedHTML.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
        // Check if target="_blank" is already present
        if (!attributes.includes('target=')) {
          return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
        }
        return match;
      });
      return (
        <div
          ref={contentRef}
          data-chat-message="true"
          className={`break-words chat-content whitespace-pre-line ${
            isUser ? 'text-white' : 'text-jobesta-gray-90'
          }`}
          dangerouslySetInnerHTML={{ __html: processedHTML }}
        />
      );
    } else {
      // Render as plain text
      return <p data-chat-message="true" className={`chat-message whitespace-pre-wrap break-words ${
        isUser ? 'text-white' : 'text-jobesta-gray-90'
      }`}>{cleanedContent}</p>;
    }
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        data-chat-message="true"
        className={`chat-message max-w-3xl px-4 py-2 rounded-jobesta ${
          isUser
            ? 'bg-jobesta-blue text-white'
            : 'bg-white border border-jobesta-gray-40 text-jobesta-gray-90'
        }`}
      >
        {renderContent(message.content)}
        {message.attachment && (
          <div className="mt-2">
            <div
              className={`text-xs flex items-center ${
                isUser ? 'text-white opacity-90' : 'text-jobesta-gray-60'
              }`}
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{message.attachment.name}</span>
              <span className="ml-1 opacity-75">({(message.attachment.size / 1024 / 1024).toFixed(1)}MB)</span>
            </div>
          </div>
        )}
        <p
          className={`text-xs mt-1 ${
            isUser ? 'text-white opacity-75' : 'text-jobesta-gray-60'
          }`}
        >
          {message.timestamp.toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
};

export default ChatMessage;